import { motion, useReducedMotion } from "framer-motion";
import { CheckCircle2, XCircle } from "lucide-react";
import { useTranslation } from "@/lib/i18n";
import { useIsMobile } from "@/hooks/use-mobile";

const FitSection = () => {
  const prefersReduced = useReducedMotion();
  const isMobile = useIsMobile();
  const t = useTranslation();
  const f = t.aiTeams.fit;

  const anim = {
    initial: prefersReduced || isMobile ? { opacity: 0 } : { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" as const },
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as const },
  };

  return (
    <section id="fit" className="py-16 bg-card/40">
      <div className="container mx-auto px-6">
        <motion.div
          initial={anim.initial}
          whileInView={anim.whileInView}
          viewport={anim.viewport}
          transition={anim.transition}
          className="mb-16 text-center"
        >
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-display tracking-tight">
            {f.heading}
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Good fit */}
          <motion.div
            initial={anim.initial}
            whileInView={anim.whileInView}
            viewport={anim.viewport}
            transition={{ delay: 0.1, ...anim.transition }}
            className="rounded-2xl border border-primary/30 bg-primary/5 p-8"
            style={{ boxShadow: "0 0 20px -5px hsl(var(--primary) / 0.15)" }}
          >
            <h3 className="font-display text-2xl mb-8 text-foreground">{f.goodFitTitle}</h3>
            <ul className="space-y-6">
              {f.goodFit.map((item, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: prefersReduced ? 0 : i * 0.06, duration: 0.4 }}
                  className="flex gap-3"
                >
                  <CheckCircle2 size={20} className="flex-shrink-0 mt-0.5 text-primary" />
                  <div>
                    <p className="font-body font-semibold text-foreground text-sm">{item.title}</p>
                    <p className="text-sm font-body text-muted-foreground leading-relaxed mt-0.5">{item.desc}</p>
                  </div>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Not fit */}
          <motion.div
            initial={anim.initial}
            whileInView={anim.whileInView}
            viewport={anim.viewport}
            transition={{ delay: 0.2, ...anim.transition }}
            className="rounded-2xl border border-border bg-card/40 p-8"
          >
            <h3 className="font-display text-2xl mb-8 text-foreground">{f.notFitTitle}</h3>
            <ul className="space-y-6">
              {f.notFit.map((item, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: prefersReduced ? 0 : i * 0.06, duration: 0.4 }}
                  className="flex gap-3"
                >
                  <XCircle size={20} className="flex-shrink-0 mt-0.5 text-destructive" />
                  <div>
                    <p className="font-body font-semibold text-foreground text-sm">{item.title}</p>
                    <p className="text-sm font-body text-muted-foreground leading-relaxed mt-0.5">{item.desc}</p>
                  </div>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default FitSection;
